import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import BASE_URL from '../url';

const TenantContext = createContext();

// Resolves any logo URL (absolute, backend uploads, public assets) to a fully accessible URL
export const resolveLogoUrl = (rawUrl) => {
  if (!rawUrl || typeof rawUrl !== 'string') return '';
  const trimmed = rawUrl.trim();
  if (!trimmed) return '';

  // 1. Absolute URLs (http://, https://, data:)
  if (/^(https?:|\/\/|data:)/i.test(trimmed)) {
    return trimmed;
  }

  // 2. Base API Origin and API base
  let apiOrigin = '';
  let apiBase = '';
  try {
    if (typeof BASE_URL !== 'undefined' && BASE_URL) {
      apiOrigin = BASE_URL.replace(/\/api\/?$/i, '').replace(/\/+$/, '');
      apiBase = BASE_URL.replace(/\/+$/, '');
    }
  } catch (_) {}

  if (!apiOrigin && typeof window !== 'undefined' && window.location) {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      apiOrigin = 'http://localhost:5000';
      apiBase = 'http://localhost:5000/api';
    } else {
      apiOrigin = window.location.origin;
      apiBase = `${window.location.origin}/api`;
    }
  }

  const apiRouteBase = apiBase && apiBase.endsWith('/api') ? apiBase : `${apiBase}/api`;

  // 3. Uploaded company logos (starts with /logos/logo_, /logos/, or /uploads/company-logos/)
  // Use /api/saas/logo/:filename endpoint so it routes cleanly through NGINX on production (devprophecyerp.com)
  if (trimmed.startsWith('/logos/logo_') || trimmed.startsWith('/uploads/company-logos/')) {
    const filename = trimmed.replace(/^.*[\\\/]/, '');
    return `${apiRouteBase}/saas/logo/${filename}`;
  }
  if (trimmed.startsWith('/logos/')) {
    const filename = trimmed.replace(/^\/logos\//, '');
    return `${apiRouteBase}/saas/logo/${filename}`;
  }
  if (trimmed.startsWith('/uploads/')) {
    return `${apiRouteBase}${trimmed}`;
  }

  // 4. Default frontend static assets (/DISENAI.png, /cognifyar_logo.png, /prophecy_favicon.png, etc.)
  return trimmed;
};

// Dynamic Favicon Helper
const setFavicon = (iconUrl) => {
  try {
    if (typeof document === 'undefined') return;
    const resolved = resolveLogoUrl(iconUrl);
    const existingIcons = document.querySelectorAll("link[rel*='icon']");
    existingIcons.forEach(el => el && el.parentNode && el.parentNode.removeChild(el));

    const link = document.createElement('link');
    link.id = 'app-favicon';
    link.rel = 'icon';
    link.type = resolved && resolved.toLowerCase().includes('.png') ? 'image/png' : 'image/x-icon';
    link.href = `${resolved}?v=${Date.now()}`;
    document.head.appendChild(link);
  } catch (e) {
    console.warn('Favicon update error:', e);
  }
};

// Subdomain parser: extracts tenant slug from hostname (e.g. cognifyar.devprophecyerp.com -> cognifyar)
export const extractSubdomain = () => {
  if (typeof window === 'undefined' || !window.location || !window.location.hostname) return null;
  const host = window.location.hostname.toLowerCase().trim();

  // If IP address, return null
  if (/^(\d{1,3}\.){3}\d{1,3}$/.test(host)) return null;

  // Reserved domain parts that are NOT client tenant slugs
  const reserved = ['www', 'devprophecyerp', 'prophecyerp', 'hub', 'api', 'dev', 'admin', 'mail', 'localhost', '127'];

  // Custom standalone client domains
  if (host.includes('disenai.com')) return 'disen';
  if (host.includes('cognifyar.com')) return 'cognifyar';

  const parts = host.split('.');
  // Example 1: cognifyar.devprophecyerp.com -> parts: ['cognifyar', 'devprophecyerp', 'com'] -> sub: 'cognifyar'
  // Example 2: disen.devprophecyerp.com -> parts: ['disen', 'devprophecyerp', 'com'] -> sub: 'disen'
  // Example 3: devprophecyerp.com -> parts: ['devprophecyerp', 'com'] -> root domain (null)
  if (parts.length >= 3) {
    const candidate = parts[0];
    if (!reserved.includes(candidate)) {
      return candidate;
    }
  } else if (parts.length === 2 && parts[1] === 'localhost') {
    const candidate = parts[0];
    if (!reserved.includes(candidate)) {
      return candidate;
    }
  }

  return null;
};

// Robust resolution of active tenant slug from Subdomain, URL search param, localStorage, or user object
export const resolveEffectiveCompanyId = (overrideCompany) => {
  if (overrideCompany && typeof overrideCompany === 'string' && overrideCompany.trim()) {
    return overrideCompany.toLowerCase().trim();
  }
  try {
    // 1. Subdomain check (Highest priority: cognifyar.devprophecyerp.com -> cognifyar)
    const sub = extractSubdomain();
    if (sub) return sub;

    // 2. URL search param (?company=slug or ?tenant=slug)
    // Allows https://devprophecyerp.com/login?company=cognifyar to continue working seamlessly!
    if (typeof window !== 'undefined' && window.location && window.location.search) {
      const params = new URLSearchParams(window.location.search);
      const urlComp = params.get('company') || params.get('tenant');
      if (urlComp && urlComp.trim()) return urlComp.toLowerCase().trim();
    }

    // 3. If authenticated session exists, respect stored session company
    const token = localStorage.getItem('token');
    if (token) {
      const c1 = localStorage.getItem('company_id');
      if (c1 && c1 !== 'undefined' && c1 !== 'null' && c1.trim()) return c1.toLowerCase().trim();

      const c2 = localStorage.getItem('saas_company_id');
      if (c2 && c2 !== 'undefined' && c2 !== 'null' && c2.trim()) return c2.toLowerCase().trim();

      const savedUser = localStorage.getItem('user');
      if (savedUser) {
        try {
          const parsed = JSON.parse(savedUser);
          if (parsed?.company_id && parsed.company_id !== 'undefined' && parsed.company_id !== 'null' && parsed.company_id.trim()) {
            return parsed.company_id.toLowerCase().trim();
          }
        } catch (e) {}
      }
    }
  } catch (e) {}
  return 'prophecy';
};

// Get instant tenant config (Synchronous from Cache or Pre-known Defaults)
export const getTenantConfigFor = (companyId) => {
  const clean = (companyId || 'prophecy').toLowerCase().trim();
  let cachedConfig = null;
  try {
    const raw = localStorage.getItem(`saas_config_${clean}`);
    if (raw) cachedConfig = JSON.parse(raw);
  } catch (e) {}

  // Only use cached config if it exactly matches this company AND is authoritative from backend
  // (not a temporary pre-fetch fallback from another session)
  if (
    cachedConfig &&
    cachedConfig.companyId === clean &&
    !cachedConfig._isFallback
  ) {
    return cachedConfig;
  }

  const isProphecy = !clean || clean === 'prophecy';
  const knownTenants = { disen: 'Disen Systems', cognifyar: 'Cognifyar Technologies' };
  const knownLogos = { disen: '/DISENAI.png', cognifyar: '/cognifyar_logo.png' };
  const defaultCompanyName = isProphecy 
    ? 'Prophecy ERP' 
    : (knownTenants[clean] || (clean.charAt(0).toUpperCase() + clean.slice(1)));
  // Only use a known static logo; for unknown tenants return null so letter badge shows immediately
  const defaultLogo = isProphecy 
    ? '/prophecy_favicon.png' 
    : (knownLogos[clean] || null);

  return {
    companyId: clean,
    companyName: defaultCompanyName,
    logoUrl: defaultLogo,
    primaryColor: isProphecy ? '#1976D2' : '#020617',
    allowedModules: isProphecy ? ['Recruitment', 'ATS', 'Invoice', 'Timesheet', 'H1B', 'CRM'] : ['Recruitment'],
    _isFallback: true  // Marks this as a pre-fetch default — do NOT persist to localStorage
  };
};

// Synchronous initial state (Zero delay / Zero flicker)
const getInitialTenant = () => {
  const companyId = resolveEffectiveCompanyId();
  const cfg = getTenantConfigFor(companyId);
  if (typeof document !== 'undefined') {
    document.title = cfg.companyName || `${companyId.toUpperCase()} ERP`;
    if (cfg.logoUrl) setFavicon(cfg.logoUrl);
  }
  return cfg;
};

export const TenantProvider = ({ children }) => {
  const [tenant, setTenant] = useState(getInitialTenant);

  const fetchBranding = useCallback((forcedCompany) => {
    try {
      const companyId = resolveEffectiveCompanyId(forcedCompany);

      // Keep localStorage in sync
      localStorage.setItem('saas_company_id', companyId);
      localStorage.setItem('company_id', companyId);

      const headers = { 'x-company-id': companyId };

      fetch(`${BASE_URL}/api/saas/config?company=${encodeURIComponent(companyId)}`, { headers })
        .then(res => res.json())
        .then(data => {
          if (data && data.success) {
            // Strip the internal fallback flag before storing real backend data
            const { _isFallback, ...cleanData } = data;
            setTenant(cleanData);
            try {
              localStorage.setItem(`saas_config_${cleanData.companyId}`, JSON.stringify(cleanData));
            } catch (e) {}
            if (cleanData.companyName) {
              document.title = cleanData.companyName;
            }
            if (cleanData.logoUrl) {
              setFavicon(cleanData.logoUrl);
            } else {
              setFavicon('/prophecy_favicon.png');
            }
          }
        })
        .catch(err => {
          console.warn('SaaS config fetch error:', err);
        });
    } catch (err) {
      console.warn('Tenant context init error:', err);
    }
  }, []);

  // Synchronous tenant switch: updates state IMMEDIATELY to prevent any flicker
  const switchTenant = useCallback((targetCompany) => {
    if (!targetCompany) return;
    const clean = targetCompany.toLowerCase().trim();
    localStorage.setItem('company_id', clean);
    localStorage.setItem('saas_company_id', clean);

    // Clear any stale saas_config cache for this company so stale logos never bleed through
    try { localStorage.removeItem(`saas_config_${clean}`); } catch (e) {}

    const instantConfig = getTenantConfigFor(clean);
    setTenant(instantConfig);

    if (typeof document !== 'undefined') {
      document.title = instantConfig.companyName;
      if (instantConfig.logoUrl) setFavicon(instantConfig.logoUrl);
    }

    // Background fetch to ensure fresh permissions & latest logos
    fetchBranding(clean);
  }, [fetchBranding]);

  useEffect(() => {
    fetchBranding();

    const handleStorage = (e) => {
      if (e.key === 'company_id' || e.key === 'saas_company_id' || e.key === 'user') {
        fetchBranding();
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [fetchBranding]);

  useEffect(() => {
    if (tenant.logoUrl) {
      setFavicon(tenant.logoUrl);
    } else {
      setFavicon('/prophecy_favicon.png');
    }
    if (tenant.companyName) {
      document.title = tenant.companyName;
    }
  }, [tenant.companyId, tenant.logoUrl, tenant.companyName]);

  return (
    <TenantContext.Provider value={{ ...tenant, refreshBranding: fetchBranding, switchTenant, resolveLogoUrl }}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => useContext(TenantContext);
